/**
 *  @class
 *  @function FacetsToggle
 */
const facetDebounce =
  window.debounce ||
  function (callback, delay) {
    let timeoutId = null;

    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        callback.apply(null, args);
      }, delay);
    };
  };

class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons
    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.getElementsByTagName('body')[0].classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = facetDebounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector('form');

    form.addEventListener('input', (event) => {
      FacetFiltersForm.captureMobileFilterIntent(event);
      this.debouncedOnSubmit(event);
    });

    form.addEventListener(
      'change',
      (event) => {
        if (!event.target.closest('.faceted-filter-group-display')) return;

        FacetFiltersForm.captureMobileFilterIntent(event);
        this.debouncedOnSubmit(event);
        event.stopImmediatePropagation();
      },
      true
    );

    form.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;

      const activeFilterLink = event.target.closest(
        '.faceted-active-filters__clear, .faceted-active-filters__remove-filter, .faceted-filter-group-display__header-clear'
      );
      if (!activeFilterLink) return;

      event.preventDefault();
      this.onActiveFilterClick(event);
    });

    form.addEventListener('submit', (event) => {
      FacetFiltersForm.captureMobileFilterIntent(event);
      this.onSubmitHandler(event);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoadingState(isLoading) {
    const collectionGrid = document.querySelector('.product-list.product-list--collection');
    const sortControls = document.querySelector('#facet-form .section_select');

    document.body.classList.toggle('collection-view-loading', isLoading);

    if (collectionGrid) {
      collectionGrid.classList.toggle('is-updating', isLoading);

      if (isLoading) {
        collectionGrid.style.setProperty('--collection-grid-lock-height', `${collectionGrid.offsetHeight}px`);
        collectionGrid.setAttribute('aria-busy', 'true');
      } else {
        collectionGrid.style.removeProperty('--collection-grid-lock-height');
        collectionGrid.removeAttribute('aria-busy');
      }
    }

    if (sortControls) {
      sortControls.classList.toggle('is-updating', isLoading);

      if (isLoading) {
        sortControls.setAttribute('aria-busy', 'true');
      } else {
        sortControls.removeAttribute('aria-busy');
      }
    }
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (sections.length === 0) {
      window.location.search = searchParams;
      return;
    }
    const keepMobileFilterOpen =
      FacetFiltersForm.keepMobileFilterOpen || FacetFiltersForm.shouldKeepMobileFilterOpen(event);
    const mobileFilterCloseCount = keepMobileFilterOpen
      ? FacetFiltersForm.mobileFilterCloseCount ?? window.PawuniCollectionFacetMenu?.getMobileCloseCount?.()
      : null;
    const container = document.getElementsByClassName('thb-filter-count');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionContainer = productGridContainer ? productGridContainer.querySelector('.collection') : null;

    FacetFiltersForm.keepMobileFilterOpen = false;
    FacetFiltersForm.mobileFilterCloseCount = null;

    FacetFiltersForm.setLoadingState(true);

    if (collectionContainer) {
      collectionContainer.classList.add('loading');
    }

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, keepMobileFilterOpen, mobileFilterCloseCount);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, keepMobileFilterOpen, mobileFilterCloseCount);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, keepMobileFilterOpen = false, mobileFilterCloseCount = null) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.setLoadingState(false);
        if (keepMobileFilterOpen) {
          FacetFiltersForm.restoreMobileFilterSheet(mobileFilterCloseCount);
        }

        new FacetsToggle();
      });
  }

  static renderSectionFromCache(filterDataUrl, event, keepMobileFilterOpen = false, mobileFilterCloseCount = null) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.setLoadingState(false);
    if (keepMobileFilterOpen) {
      FacetFiltersForm.restoreMobileFilterSheet(mobileFilterCloseCount);
    }
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const nextGrid = parsedHTML.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');

    if (!nextGrid || !currentGrid) return;

    currentGrid.innerHTML = nextGrid.innerHTML;
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static shouldKeepMobileFilterOpen(event) {
    const mobileMenu = event?.target?.closest?.('#toggle-facet-menu-mobile.collection-mobile-filter');
    const mobileFilterIsOpen =
      document.body.classList.contains('collection-mobile-filter-open') ||
      (mobileMenu && !mobileMenu.classList.contains('hidden'));

    return mobileFilterIsOpen && window.matchMedia('(max-width: 798px)').matches;
  }

  static captureMobileFilterIntent(event) {
    const shouldKeepOpen = FacetFiltersForm.shouldKeepMobileFilterOpen(event);
    FacetFiltersForm.keepMobileFilterOpen = shouldKeepOpen;
    FacetFiltersForm.mobileFilterCloseCount = shouldKeepOpen
      ? window.PawuniCollectionFacetMenu?.getMobileCloseCount?.()
      : null;
  }

  static restoreMobileFilterSheet(mobileFilterCloseCount = null) {
    const restore = () => {
      if (
        mobileFilterCloseCount !== null &&
        window.PawuniCollectionFacetMenu?.getMobileCloseCount &&
        window.PawuniCollectionFacetMenu.getMobileCloseCount() !== mobileFilterCloseCount
      ) {
        return;
      }

      if (window.PawuniCollectionFacetMenu?.openMobile) {
        window.PawuniCollectionFacetMenu.openMobile({ focus: false });
        return;
      }

      const mobileMenu = document.getElementById('toggle-facet-menu-mobile');
      if (!mobileMenu) return;

      mobileMenu.classList.remove('hidden');
      document.body.classList.add('collection-mobile-filter-open');

      const facetButton = document.getElementById('facet-button');
      if (facetButton) {
        facetButton.setAttribute('aria-expanded', 'true');
      }
    };

    restore();
    window.requestAnimationFrame(restore);
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid') || document.getElementById('ProductGridContainer');
    if (!productGrid?.dataset?.id) return [];

    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === '0.00') {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (
        searchParams.get('filter.v.price.lte') ===
        parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)
      ) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const filterLink = event.currentTarget?.href ? event.currentTarget : event.target.closest('a');
    if (!filterLink) return;

    FacetFiltersForm.captureMobileFilterIntent(event);
    FacetFiltersForm.toggleActiveFacets();
    const url = filterLink.href.indexOf('?') == -1 ? '' : filterLink.href.slice(filterLink.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.keepMobileFilterOpen = false;
FacetFiltersForm.mobileFilterCloseCount = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        range: {
          min: 0,
          max: parseFloat(slider.dataset.max),
        },
      },
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });

    slider.noUiSlider.on('change', function () {
      form.querySelector('form').dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    });
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
});
